import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { DropdownOption } from "../../../DropDownV2";
import type { ClientTypeFilterValue } from "../../ClientTypeFilter";
import type { ClientTradingTableFilterSubProps } from "./types";

import { Button as ButtonV2 } from "../../../Button";
import { ExportCSVButton } from "../../../Button/ExportCSVButton";
import DatepickerV2 from "../../../DatepickerV2";
import { FieldTrigger } from "../../../DatepickerV2/components/FieldTrigger";
import { getShortcutButtonLabel } from "../../../DatepickerV2/helpers/shortcutLabel";
import { useDatepickerTranslations } from "../../../hooks/useDatepickerTranslations";
import { FilterIcon } from "../../../Icons";
import { SearchInputV2 } from "../../../SearchInputV2";
import { ALL_TIME_START } from "../../../SubIBReport/providers/getDefaultDateRange";
import { Typo } from "../../../Typo";
import { ALL_CLIENT_TYPES } from "../../ClientTypeFilter";
import { useClientTradingReportContext } from "../providers/TradingReportProvider";
import { ClientTradingFilterModal } from "./FilterModal";

export const DesktopFilters = ({
  handleDateRangePicker,
  handleExportButton,
  handleSearchInput,
  handleRebateAccountsDropdown,
  handleRebateProgramsMultiSelect,
  canViewPII,
  redactedFields,
  translations,
  onDateTooltipHover,
  showRebateProgramFilter,
  showToolbarDateRange,
  showClientTypeFilter = false,
  subIBsLoadFailed,
  onRetrySubIBs,
  toolbarLeading,
}: ClientTradingTableFilterSubProps) => {
  const { t } = useTranslation();
  const { translatedContent } = translations;
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [todayAnchor] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  const {
    state: {
      searchQuery,
      dateRange,
      selected: {
        rebateAccountIDs: selectedRebateAccountIDs,
        rebateProgramIDs: selectedRebateProgramIDs,
        clientTypes: selectedClientTypeValues,
        subIBIDs: selectedSubIBIDs,
      },
    },
    options: { rebateAccountIDs, rebateProgramIDs, subIBIDs },
    actions,
  } = useClientTradingReportContext();

  const [search, setSearch] = useState(searchQuery);

  const selectedRebateAccounts = useMemo(() => {
    return ((selectedRebateAccountIDs as string[]) ?? []).map((id) => ({
      label:
        rebateAccountIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [rebateAccountIDs, selectedRebateAccountIDs]);

  const selectedRebatePrograms = useMemo(() => {
    return ((selectedRebateProgramIDs as string[]) ?? []).map((id) => ({
      label:
        rebateProgramIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [rebateProgramIDs, selectedRebateProgramIDs]);

  const selectedClientTypes = useMemo(
    () =>
      (selectedClientTypeValues as ClientTypeFilterValue[] | null) ??
      ALL_CLIENT_TYPES,
    [selectedClientTypeValues],
  );

  const selectedSubIBs = useMemo(() => {
    return ((selectedSubIBIDs as string[]) ?? []).map((id) => ({
      label: subIBIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [subIBIDs, selectedSubIBIDs]);

  const handleDropdownChange = (options: DropdownOption[]) => {
    handleRebateAccountsDropdown?.(options);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearchInput?.(value);
  };

  // An unselected rebate axis means "no restriction", so it counts only once
  // picked. The modal's date field (flag off) always has a value and always counts.
  const filterCount =
    (showToolbarDateRange ? 0 : 1) +
    (selectedRebateAccountIDs &&
    (selectedRebateAccountIDs as string[]).length > 0
      ? 1
      : 0) +
    (showRebateProgramFilter &&
    selectedRebateProgramIDs &&
    (selectedRebateProgramIDs as string[]).length > 0
      ? 1
      : 0) +
    (showClientTypeFilter && selectedClientTypes.length === 1 ? 1 : 0) +
    (showClientTypeFilter && selectedSubIBs.length > 0 ? 1 : 0);

  const searchTitle = useMemo(() => {
    if (canViewPII) {
      if (redactedFields?.includes("name")) {
        return translatedContent?.filters?.searchWithPIIEmail;
      }
      if (redactedFields?.includes("email")) {
        return translatedContent?.filters?.searchWithPIIName;
      }
      return translatedContent?.filters?.search;
    }
    return translatedContent?.filters?.searchNoPII;
  }, [canViewPII, redactedFields, translatedContent]);

  const baseDatepickerConfigs = useDatepickerTranslations();
  const dateAllTimeLabel = t(
    "clientsPage_tradingReport_filter_date_option_allTime",
    "All time",
  );
  const datepickerConfigs = useMemo(
    () => ({
      ...baseDatepickerConfigs,
      shortcuts: {
        allTime: {
          text: dateAllTimeLabel,
          period: {
            start: ALL_TIME_START,
            end: todayAnchor,
          },
        },
        ...baseDatepickerConfigs.shortcuts,
      },
    }),
    [baseDatepickerConfigs, dateAllTimeLabel, todayAnchor],
  );

  const datepickerValue = {
    startDate: dateRange.from,
    endDate: dateRange.to,
  };
  const dateShortcutLabel = getShortcutButtonLabel(
    datepickerValue,
    datepickerConfigs,
  );
  // "All time" isn't a bounded range, so the calendar highlights nothing; the
  // trigger label still comes from the real `datepickerValue` above.
  const calendarValue =
    dateShortcutLabel === dateAllTimeLabel
      ? { startDate: null, endDate: null }
      : datepickerValue;
  const calendarStartFrom =
    dateShortcutLabel === dateAllTimeLabel ? todayAnchor : undefined;
  const dateRangePlaceholder =
    translatedContent.filters?.dataTimeRangeLabel ??
    t("clientsPage_tradingReport_filter_date_placeholder", "Select dates");

  return (
    <div data-testid="desktop-filters" className="mt-10 flex flex-col">
      <div className="flex grow flex-row items-center gap-4">
        {toolbarLeading && <div className="shrink-0">{toolbarLeading}</div>}
        <div className="w-full max-w-[440px]">
          <SearchInputV2
            value={search}
            onChange={handleSearchChange}
            placeholder={searchTitle}
          />
        </div>
        {showToolbarDateRange && (
          <DatepickerV2
            value={calendarValue}
            startFrom={calendarStartFrom}
            onChange={(range) =>
              handleDateRangePicker?.({
                startDate: range?.startDate ?? null,
                endDate: range?.endDate ?? null,
              })
            }
            useRange
            showShortcuts
            separator="-"
            displayFormat="DD MMM YYYY"
            showFooter
            readOnly
            maxDate={new Date()}
            configs={datepickerConfigs}
            classNames={{
              footer: () =>
                "flex items-center justify-end pb-2.5 pt-3 border-t border-stroke-primary [&_div]:rtl:gap-2 [&_button]:rounded-md",
            }}
            inputId="client-trading-report-date-range"
            placeholder={dateRangePlaceholder}
            containerClassName="relative"
            trigger={(triggerProps) => (
              <FieldTrigger
                {...triggerProps}
                allowClear={false}
                placeholder={dateRangePlaceholder}
                shortcutButtonLabel={dateShortcutLabel}
                data-testid="client_trading_report_toolbar_date_range_select"
              />
            )}
          />
        )}
        <ButtonV2
          variant="tonal"
          size="md"
          onClick={() => setIsFilterOpen(true)}
          data-testid="client_trading_report_toolbar_filter_button"
        >
          <span className="flex items-center gap-2">
            <FilterIcon
              className="h-4 w-4"
              aria-hidden="true"
              focusable="false"
            />
            <Typo element="span" type="labelSM">
              {translatedContent.filters?.filterButton ?? "Filter"}
            </Typo>
            {filterCount > 0 && (
              <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-surface-brand-default px-1.5">
                <Typo
                  element="span"
                  type="label2XS"
                  textColor="text-text-inverse-primary"
                >
                  {filterCount}
                </Typo>
              </span>
            )}
          </span>
        </ButtonV2>
        <div className="ml-auto">
          <ExportCSVButton
            label={translatedContent.exportCSVText}
            onClick={() => handleExportButton?.()}
            data-testid="client_trading_report_toolbar_export_csv_button"
          />
        </div>
      </div>

      <ClientTradingFilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        rebateAccountOptions={rebateAccountIDs}
        selectedRebateAccounts={selectedRebateAccounts}
        onRebateAccountsChange={handleDropdownChange}
        rebateProgramOptions={rebateProgramIDs}
        selectedRebatePrograms={selectedRebatePrograms}
        onRebateProgramsChange={(options) =>
          handleRebateProgramsMultiSelect?.(options)
        }
        translations={translations}
        showRebateProgramFilter={showRebateProgramFilter}
        showClientTypeFilter={showClientTypeFilter}
        subIBsLoadFailed={subIBsLoadFailed}
        onRetrySubIBs={onRetrySubIBs}
        selectedClientTypes={selectedClientTypes}
        onClientTypesChange={(clientTypes) =>
          actions.setSelected("clientTypes", clientTypes)
        }
        subIBOptions={subIBIDs}
        selectedSubIBs={selectedSubIBs}
        onSubIBsChange={(subIBs) =>
          actions.setSelected(
            "subIBIDs",
            subIBs.map((subIB) => subIB.value),
          )
        }
        {...(!showToolbarDateRange && {
          dateRange: { startDate: dateRange.from, endDate: dateRange.to },
          onDateRangeChange: handleDateRangePicker,
          onDateTooltipHover,
        })}
      />
    </div>
  );
};
